import { CustomDatePicker, DateBy } from '../../../common/datepicker/types';
import { PromptWithCategory } from '../types';
import { isSweepDefaultPrompt, SWEEP_AUTHOR_ID } from '@server/ai-prompts.config';
import { UNCATEGORIZED_FILTER_ID } from '../usePromptGroupState';

interface FilterPromptsParams {
  prompts: PromptWithCategory[];
  searchTerm: string;
  selectedCategoryFilters: string[];
  selectedUserFilters: string[];
  dateFilter: CustomDatePicker | null;
}

export const filterPrompts = ({
  prompts,
  searchTerm,
  selectedCategoryFilters,
  selectedUserFilters,
  dateFilter,
}: FilterPromptsParams): PromptWithCategory[] => {
  let filtered = prompts;

  if (searchTerm) {
    const lowerSearch = searchTerm.toLowerCase();
    filtered = filtered.filter(
      (prompt) =>
        prompt.name.toLowerCase().includes(lowerSearch) ||
        prompt.promptText.toLowerCase().includes(lowerSearch) ||
        prompt.categoryName.toLowerCase().includes(lowerSearch),
    );
  }

  if (selectedCategoryFilters.length > 0) {
    filtered = filtered.filter((prompt) => {
      if (selectedCategoryFilters.includes(UNCATEGORIZED_FILTER_ID) && !prompt.promptGroupId) {
        return true;
      }
      return selectedCategoryFilters.includes(prompt.categoryId);
    });
  }

  if (selectedUserFilters.length > 0) {
    filtered = filtered.filter((prompt) => {
      if (prompt.updatedById) {
        return selectedUserFilters.includes(prompt.updatedById);
      }
      const isSweepPrompt = isSweepDefaultPrompt(
        prompt.name,
        prompt.categoryName,
        prompt.updatedById,
      );
      if (isSweepPrompt) {
        return selectedUserFilters.includes(SWEEP_AUTHOR_ID);
      }
      return selectedUserFilters.includes('__blank__');
    });
  }

  if (dateFilter) {
    const fromDate = dateFilter[DateBy.From] ? new Date(dateFilter[DateBy.From]) : null;
    const toDate = dateFilter[DateBy.To] ? new Date(dateFilter[DateBy.To]) : null;

    filtered = filtered.filter((prompt) => {
      const promptDate = new Date(prompt.updatedAt);
      if (fromDate && promptDate < fromDate) return false;
      if (toDate && promptDate > toDate) return false;
      return true;
    });
  }

  return filtered;
};

export const promptTableColumns = [
  {
    field: 'name',
    headerName: 'Prompt Name',
    minWidth: '210px',
    showSortIcon: true,
  },
  {
    field: 'categoryName',
    headerName: 'Category',
    minWidth: '200px',
    showSortIcon: false,
  },
  {
    field: 'updatedByName',
    headerName: 'Last modified by',
    showSortIcon: false,
  },
  {
    field: 'updatedAt',
    headerName: 'Last modified',
    showSortIcon: false,
  },
];

interface PromptsEmptyStateParams {
  hasPrompts: boolean;
  hasActiveFilters: boolean;
}

export const getPromptsEmptyState = ({ hasPrompts, hasActiveFilters }: PromptsEmptyStateParams) => {
  if (!hasPrompts) {
    return {
      title: 'The category is empty.',
      description: 'Add a prompt to get started.',
    };
  }

  if (hasActiveFilters) {
    return {
      title: 'No prompts found.',
      description: 'Try a different search or add a new prompt to get started.',
    };
  }

  return {
    title: 'No prompts found.',
    description: 'Try a different search or add a new prompt to get started.',
  };
};
